import { useContext, useState, useEffect } from "react";
import { Modal, Button, Typography } from "antd";
import { useUserInfo } from "../../contexts/userInfo";
import { useSubscriptions } from "../../contexts/subscriptionsContext";
import { getFreeTrialDaysLeft } from "../../helper/getFreeTrialsDayLeft";
import { hasFreeTrialExpired } from "../../helper/hasFreeTrialExpired";
import { useNavigate } from "react-router";
import moment from "moment";

const { Title, Paragraph, Text } = Typography;

const SubscriptionAlertModal = () => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const { userInfo } = useUserInfo();
  const { purchasedSubscription, loading: subsLoading } = useSubscriptions();

  const createdAt = userInfo?.created_at;

  // trial values (use helpers)
  const trialDaysLeft = createdAt ? getFreeTrialDaysLeft(createdAt) : 0;
  const trialExpired = createdAt ? hasFreeTrialExpired(createdAt) : true;

  // subscription values
  const subscription = purchasedSubscription || null;
  const subscriptionEndRaw = subscription?.user_subscription_end_date || null;
  const subscriptionEnd = subscriptionEndRaw
    ? moment(subscriptionEndRaw)
    : null;
  const daysUntilSubscriptionEnd = subscriptionEnd
    ? subscriptionEnd.diff(moment(), "days")
    : null;
  const subscriptionIsActive = subscription?.is_active === 1;
  const subscriptionCancelledButActiveUntilEnd =
    subscription &&
    subscription?.is_active === 0 &&
    subscriptionEnd &&
    daysUntilSubscriptionEnd >= 0;

  // decide which modal (if any) should show
  const TRIGGER_DAYS = 7; // threshold for warning before end (adjustable)
  const shouldShowTrialModal =
    !subscription && !trialExpired && trialDaysLeft > 0;
  const shouldShowEndingSubscriptionModal =
    subscriptionCancelledButActiveUntilEnd &&
    daysUntilSubscriptionEnd <= TRIGGER_DAYS;

  // keep hooks unconditionally before any early returns
  useEffect(() => {
    if (shouldShowTrialModal || shouldShowEndingSubscriptionModal) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [shouldShowTrialModal, shouldShowEndingSubscriptionModal]);

  // safe early returns
  if (subsLoading) return null;
  // if user has an active subscription, no popup
  if (subscriptionIsActive) return null;
  // need either trial data or a cancelled-but-active subscription to show anything
  if (!shouldShowTrialModal && !shouldShowEndingSubscriptionModal) return null;

  // content helpers
  const trialEndDate = createdAt
    ? moment(createdAt).add(7, "days").format("D MMMM YYYY")
    : null;
  const handleSubscribe = () => {
    navigate("/pricing");
  };
  const handleMaybeLater = () => {
    setOpen(false);
  };

  return (
    <Modal
      title={
        shouldShowEndingSubscriptionModal
          ? "Your subscription will end soon"
          : `${
              trialDaysLeft > 5
                ? "Welcome Aboard!"
                : "Your free trial is ending soon"
            }`
      }
      open={open}
      onCancel={handleMaybeLater}
      footer={[
        <Button key="later" onClick={handleMaybeLater}>
          Maybe later
        </Button>,
        <Button key="subscribe" type="primary" onClick={handleSubscribe}>
          {shouldShowEndingSubscriptionModal ? "Renew" : "Subscribe"}
        </Button>,
      ]}
    >
      {shouldShowEndingSubscriptionModal ? (
        <>
          <Title level={4}>
            Subscription ending on {subscriptionEnd?.format("D MMMM YYYY")}
          </Title>
          <Paragraph>
            Your subscription was cancelled but you still have access until{" "}
            <Text strong>{subscriptionEnd?.format("D MMMM YYYY")}</Text>. That's{" "}
            {daysUntilSubscriptionEnd} day
            {daysUntilSubscriptionEnd === 1 ? "" : "s"} left.
          </Paragraph>
          <Paragraph>
            To keep uninterrupted access to premium features (shared calendars,
            advanced reminders, AI assistant, etc.), please renew your
            subscription before it ends.
          </Paragraph>
        </>
      ) : (
        <>
          <Title level={4}>Welcome to Quelld!</Title>
          <Paragraph>
            We're excited to have you exploring all that Quelld has to offer.
            You're currently enjoying a <Text strong>free trial</Text> that
            gives you access to our premium features.
          </Paragraph>
          <Paragraph>
            Your trial will end on <Text strong>{trialEndDate}</Text> —{" "}
            {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left to make the
            most of it.
          </Paragraph>
          <Paragraph>
            Upgrade anytime to keep using powerful tools like shared calendars,
            advanced reminders, and AI-powered features.
          </Paragraph>
          <Paragraph>
            <Text type="secondary">
              Don’t worry — you won't be charged until your trial ends.
            </Text>
          </Paragraph>
        </>
      )}
    </Modal>
  );
};

export default SubscriptionAlertModal;
